import type { ImageRequirements } from '@/api/types';

export const IMAGE_FORMATS = [
  { value: 'jpeg', label: 'JPG', mime: 'image/jpeg' },
  { value: 'png', label: 'PNG', mime: 'image/png' },
  { value: 'webp', label: 'WebP', mime: 'image/webp' },
] as const;

export const NO_IMAGE_REQUIREMENTS: ImageRequirements = {
  maxFileSizeMb: null,
  minLongSidePx: null,
  maxLongSidePx: null,
  minDpi: null,
  requireExif: false,
  allowedFormats: [],
  rejectNonCompliant: true,
};

/** Human-readable (Slovenian) list of the limits that are set, e.g. for the landing page and above the upload form. */
export function describeImageRequirements(req: ImageRequirements | null | undefined): string[] {
  if (!req) return [];
  const lines: string[] = [];

  if (req.allowedFormats.length > 0) {
    const labels = req.allowedFormats.map((f) => IMAGE_FORMATS.find((x) => x.value === f)?.label ?? f.toUpperCase());
    lines.push(`Format: ${labels.join(', ')}`);
  }
  if (req.minLongSidePx != null && req.minLongSidePx === req.maxLongSidePx) {
    lines.push(`Daljša stranica: ${req.minLongSidePx} px`);
  } else {
    if (req.minLongSidePx != null) lines.push(`Daljša stranica: vsaj ${req.minLongSidePx} px`);
    if (req.maxLongSidePx != null) lines.push(`Daljša stranica: največ ${req.maxLongSidePx} px`);
  }
  if (req.minDpi != null) lines.push(`Ločljivost: vsaj ${req.minDpi} dpi`);
  if (req.maxFileSizeMb != null) lines.push(`Velikost datoteke: največ ${req.maxFileSizeMb} MB`);
  if (req.requireExif) lines.push('Slika mora vsebovati podatke EXIF');
  return lines;
}

/** The `accept` attribute of the file input: the allowed formats, or any image. */
export function acceptAttribute(req: ImageRequirements | null | undefined): string {
  if (!req || req.allowedFormats.length === 0) return 'image/*';
  return req.allowedFormats
    .map((f) => IMAGE_FORMATS.find((x) => x.value === f)?.mime)
    .filter(Boolean)
    .join(',');
}

function readDimensions(file: File): Promise<{ width: number; height: number } | null> {
  return new Promise((resolve) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(url);
      resolve({ width: img.naturalWidth, height: img.naturalHeight });
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      resolve(null);
    };
    img.src = url;
  });
}

/**
 * Early check of what the browser can see for itself (format, size, long side) so the competitor does not wait for an
 * upload to learn that the file will be refused. Resolution (dpi) and EXIF are only checked by the server.
 * Returns the problems found; empty = the file looks fine.
 */
export async function checkImageFile(file: File, req: ImageRequirements | null | undefined): Promise<string[]> {
  if (!req) return [];
  const problems: string[] = [];

  if (req.allowedFormats.length > 0) {
    const format = IMAGE_FORMATS.find((f) => f.mime === file.type)?.value;
    if (!format || !req.allowedFormats.includes(format)) {
      const labels = req.allowedFormats.map((f) => IMAGE_FORMATS.find((x) => x.value === f)?.label ?? f);
      problems.push(`Slika mora biti v formatu ${labels.join(', ')}.`);
    }
  }
  if (req.maxFileSizeMb != null && file.size > req.maxFileSizeMb * 1024 * 1024) {
    const mb = (file.size / (1024 * 1024)).toFixed(1).replace('.', ',');
    problems.push(`Slika je prevelika (${mb} MB). Največja dovoljena velikost je ${req.maxFileSizeMb} MB.`);
  }

  if (req.minLongSidePx != null || req.maxLongSidePx != null) {
    const dims = await readDimensions(file);
    if (dims) {
      const longSide = Math.max(dims.width, dims.height);
      if (req.minLongSidePx != null && req.minLongSidePx === req.maxLongSidePx) {
        if (longSide !== req.minLongSidePx) {
          problems.push(`Daljša stranica slike mora imeti natančno ${req.minLongSidePx} px (vaša ima ${longSide} px).`);
        }
      } else {
        if (req.minLongSidePx != null && longSide < req.minLongSidePx) {
          problems.push(`Daljša stranica slike mora imeti vsaj ${req.minLongSidePx} px (vaša ima ${longSide} px).`);
        }
        if (req.maxLongSidePx != null && longSide > req.maxLongSidePx) {
          problems.push(`Daljša stranica slike sme imeti največ ${req.maxLongSidePx} px (vaša ima ${longSide} px).`);
        }
      }
    }
  }
  return problems;
}
